import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  IconButton, Typography, Chip, Button, Stack, CircularProgress,
  Alert, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';
import {
  Search, Add, Edit, Delete, Refresh, Clear, Save, Cancel
} from '@mui/icons-material';

const Personnel = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    id_employe: '', nom: '', prenom: '', sexe: 'M',
    date_naissance: '', cnie: '', fonction: '', pays: 'Maroc'
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/personnel', { headers: getAuthHeaders() });
      setData(res.data?.data || res.data || []);
      setError('');
    } catch (err) {
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item => {
    const s = searchTerm.toLowerCase();
    return (
      (item.nom || '').toLowerCase().includes(s) ||
      (item.prenom || '').toLowerCase().includes(s) ||
      (item.fonction || '').toLowerCase().includes(s) ||
      (item.id_employe || '').toLowerCase().includes(s) ||
      (item.cnie || '').toLowerCase().includes(s)
    );
  });

  const resetForm = () => {
    setFormData({
      id_employe: '', nom: '', prenom: '', sexe: 'M',
      date_naissance: '', cnie: '', fonction: '', pays: 'Maroc'
    });
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    if (item) {
      setSelectedItem(item);
      setFormData({
        id_employe: item.id_employe || '',
        nom: item.nom || '',
        prenom: item.prenom || '',
        sexe: item.sexe || 'M',
        date_naissance: item.date_naissance || '',
        cnie: item.cnie || '',
        fonction: item.fonction || '',
        pays: item.pays || 'Maroc'
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'add') {
        await axios.post('/api/personnel', formData, { headers: getAuthHeaders() });
        setSuccessMessage('Personnel ajouté avec succès');
      } else {
        await axios.put(`/api/personnel/${selectedItem._id}`, formData, { headers: getAuthHeaders() });
        setSuccessMessage('Personnel modifié avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'opération");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer ${item.nom} ${item.prenom} ?`)) return;
    try {
      await axios.delete(`/api/personnel/${item._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Personnel supprimé avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage('')}
        message={successMessage} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}
        message={error} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            Gestion du Personnel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} personne(s) trouvée(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button variant="contained" startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Nouveau Personnel
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth placeholder="Rechercher par nom, prénom, fonction..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>ID Employé</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Prénom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Sexe</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>CNIE</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fonction</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Pays</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={item._id} hover>
                <TableCell>{item.id_employe}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{item.nom}</TableCell>
                <TableCell>{item.prenom}</TableCell>
                <TableCell>
                  <Chip label={item.sexe === 'M' ? 'Masculin' : 'Féminin'} size="small"
                    color={item.sexe === 'M' ? 'info' : 'warning'} variant="outlined" />
                </TableCell>
                <TableCell>{item.cnie || '-'}</TableCell>
                <TableCell>{item.fonction || '-'}</TableCell>
                <TableCell>{item.pays || '-'}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Modifier">
                      <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun personnel trouvé</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination component="div" count={filteredData.length} page={page}
        onPageChange={(e, np) => setPage(np)} rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page" sx={{ mt: 2 }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? 'Ajouter un personnel' : "Modifier le personnel"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="ID Employé" value={formData.id_employe}
                onChange={e => setFormData({ ...formData, id_employe: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Sexe</InputLabel>
                <Select value={formData.sexe} label="Sexe"
                  onChange={e => setFormData({ ...formData, sexe: e.target.value })}>
                  <MenuItem value="M">Masculin</MenuItem>
                  <MenuItem value="F">Féminin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Nom *" value={formData.nom}
                onChange={e => setFormData({ ...formData, nom: e.target.value })} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Prénom *" value={formData.prenom}
                onChange={e => setFormData({ ...formData, prenom: e.target.value })} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="CNIE" value={formData.cnie}
                onChange={e => setFormData({ ...formData, cnie: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Date de naissance" type="date" value={formData.date_naissance}
                onChange={e => setFormData({ ...formData, date_naissance: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Fonction" value={formData.fonction}
                onChange={e => setFormData({ ...formData, fonction: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Pays" value={formData.pays}
                onChange={e => setFormData({ ...formData, pays: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Personnel;


